import { useState, useEffect, useCallback, useMemo } from "react";
import { ICrewBase } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
import { getCrewBases } from "src/redux/actions/Structure.Actions";
import { formatFunction, parseFunction } from "./helpers";

interface ICrewBaseSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  activeOnly?: boolean;
  /** Shows a saved value that is not a crew base rather than looking unset. Only crew bases are offered to pick. */
  keepUnlistedValue?: boolean;
}

/**
 * Sepcialized wrapper for "built-in" oc crew bases select field.
 * @param props -
 */
export const CrewBaseSelectField = (props: ICrewBaseSelectFieldProps): JSX.Element => {
  const {
    mosaicPane,
    label,
    name,
    onChange,
    valueKey,
    isMulti,
    defaultValue,
    activeOnly,
    keepUnlistedValue,
    ...selectProps
  } = props;

  ///////////////////////////////
  // MANAGE CREW BASES LIST
  ///////////////////////////////
  const [crewBases, setCrewBases] = useState<ICrewBase[]>([]);
  // Apart from the list, since an empty list (or a failed load) is still loaded.
  const [basesLoaded, setBasesLoaded] = useState<boolean>(false);

  useEffect(() => {
    const getCrewBasesAction = async (): Promise<void> => {
      const retrievedCrewBases = await getCrewBases();
      setCrewBases(retrievedCrewBases);
      setBasesLoaded(true);
    };
    getCrewBasesAction();
  }, []);

  // A saved value no crew base has, standing in for one, once the bases have loaded.
  const unlisted = useMemo((): Pick<ICrewBase, "_id" | "ICAO" | "name">[] => {
    const value = defaultValue as string | undefined;
    if (!keepUnlistedValue || !valueKey || isMulti || !value || !basesLoaded) return [];
    if (crewBases.some((base) => base[valueKey as keyof ICrewBase] === value)) return [];
    return [{ _id: value, ICAO: value, name: "not a crew base", [valueKey]: value }];
  }, [keepUnlistedValue, valueKey, isMulti, defaultValue, crewBases, basesLoaded]);

  // Filter crew bases if activeBasesOnly is true
  const filteredCrewBases = activeOnly
    ? crewBases.filter((base) => base.isActive === undefined || base.isActive === true) // Assuming isActive is the key for active bases
    : crewBases;

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, [...crewBases, ...unlisted], valueKey, isMulti),
    [crewBases, unlisted, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Airport Selector"}
      isMulti={isMulti}
      options={filteredCrewBases}
      getOptionLabel={(base: ICrewBase) => `${base.ICAO} - ${base.name}`}
      getOptionValue={(base: ICrewBase) => base._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      defaultValue={defaultValue}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Crew Base${isMulti ? "s" : ""}`}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
